import * as React from 'react';
import ConfirmDialog from 'components/ConfirmDialog';
import { confirmDialogState, claimConfirmDialogRendering } from 'state/confirm-dialog';

/**
 * Global ConfirmDialog component that renders based on the confirm-dialog state.
 * This should be rendered once at the app root level.
 *
 * @example
 * ```tsx
 * import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <GlobalConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 */
export interface IGlobalConfirmDialogProps {
  /**
   * Optional callback that returns whether the host web component is visible.
   * When multiple web component roots share the same dialog state, a hidden
   * host (e.g. `<e3d-sharing-dashboard hidden>`) should NOT claim rendering
   * because the dialog, even with `position: fixed`, is not interactive
   * inside a hidden shadow root.
   */
  isHostVisible?: () => boolean;
}

export default function GlobalConfirmDialog({ isHostVisible }: IGlobalConfirmDialogProps = {}) {
  const dialogState = confirmDialogState.useState();
  // Track whether THIS instance (not any instance) has claimed rendering rights.
  // Multiple web component roots share the same confirmDialogState but only one
  // should render at a time. useEffect (not useMemo) is used here because
  // claimConfirmDialogRendering() is a side effect that mutates shared state.
  const [canRender, setCanRender] = React.useState(false);

  React.useEffect(() => {
    if (dialogState.isOpen && !dialogState.isBeingRendered) {
      // Only claim rendering if the host web component is visible (or no check provided)
      if ((!isHostVisible || isHostVisible()) && claimConfirmDialogRendering()) {
        setCanRender(true);
      }
    } else if (!dialogState.isOpen) {
      setCanRender(false);
    }
  }, [dialogState.isOpen, dialogState.isBeingRendered, isHostVisible]);

  if (!canRender || !dialogState.isOpen) {
    return null;
  }

  return (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
      data-testid={dialogState['data-testid']}
    >
      {dialogState.children}
    </ConfirmDialog>
  );
}
